import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import InfoPopUp from "../../Common/InfoPopUp";
import { OptionDataContenxt } from "./OptionChartDashboard";
import InfoIcon from "@mui/icons-material/Info";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import * as Stomp from "stompjs";
import * as SockJS from "sockjs-client";

const title = "Orderbook";
const description =
  "Sell offers are displayed on top in red color, and Buy offers are displayed at the bottom in green color. The right side of the order book is displaying the current 50 trades that took place in the market in the last 24 hours.";

const currencySymbols = {
  INR: "₹",
  USD: "$",
  USDT: "$",
  AED: "د.إ",
  EUR: "€",
  SGD: "S$",
};

const OptionOrderBook = () => {
  const {
    chartData,
    setFinalBiddata,
    setFinalAskdata,
    basecurrency,
    lowPrice,
    highPrice,
    assetpair,
    refresh,
    setSellData,
    setBuyData,
    setLastAskItem,
    setFirstBidItem,
    } = useContext(OptionDataContenxt);

    const theme = localStorage.getItem("theme");
    const tableContentRef = useRef(null);
    const { brokerBasicInfo } = useSelector((state) => state);

    const [biddata, setBiddata] = useState([]);
    const [askdata, setAskdata] = useState([]);

    useEffect(() => {
    if (askdata && askdata.length > 0) {
      const sortedAsk = [...askdata].sort((a, b) => b.price - a.price);
      setLastAskItem(sortedAsk[sortedAsk.length - 1]);
    }
    }, [askdata]);

    useEffect(() => {
    if (biddata && biddata.length > 0) {
      const sortedBid = [...biddata].sort((a, b) => a.price - b.price);
      setFirstBidItem(sortedBid[0]);
    }
    }, [biddata]);

    const [tradep, setTradep] = useState(6);
  const [data, setData] = useState({});
  const [marketTradeRecords, setMarketTradeRecords] = useState([]);
  const [show, setShow] = useState(false);
  const [toggle, setToggle] = useState(true);

  const handleClose = () => setShow(false);
  const accessToken = localStorage.getItem("access_token");

  const websocketRef = useRef(null);
  const [stompClient, setStompClient] = useState(null);
  const webSocketEndPoint =
    "https://options-socket.paybito.com/oSocketStream/ws";
  const topic = "/user/topic/stream";
  let reqNo = Math.floor(Math.random() * 1000) + 1;

  const connect = () => {
    if (!accessToken) return;
    let ws = new SockJS(webSocketEndPoint);
    let client = Stomp.over(ws);
    client.connect(
      {},
      () => {
        websocketRef.current.socket = client;
        client.subscribe(topic, (sdkEvent) => {
          const message = JSON.parse(sdkEvent.body);
          if (websocketRef.current.subscribeMessage) {
            websocketRef.current.subscribeMessage(message);
          }
          onMessageReceived(sdkEvent);
        });
      },
      (error) => {
        console.error("WebSocket Error:", error);
        setTimeout(connect, 5000);
      }
    );
    client.debug = null;
    setStompClient(client);
  };

  const disconnect = () => {
    if (stompClient) stompClient.disconnect();
  };

  const send = (message) => {
    if (stompClient)
      stompClient.send("/app/sendRequest", {}, JSON.stringify(message));
  };

  const onMessageReceived = (message) => {
    const obj = JSON.parse(message.body);
    const parsedData = obj;
    const tradeHistoryData = obj;

    setData((prevData) => {
      if (parsedData.tR) {
        const data = parsedData.tR;
        const roc = parseFloat(data.roc).toFixed(2);
        return {
          ...prevData,
          ltpdata: data.ltp,
          lowprice: parseFloat(data.lowPrice).toFixed(2),
          highprice: parseFloat(data.highPrice).toFixed(2),
          ACTION: data.action,
          rocdata: roc,
          volumndata: parseFloat(data.volume).toFixed(2),
          rocreact: roc > 0,
          negetive: roc < 0,
          react: data.action === "sell",
        };
      }
      return prevData;
    });

    if (tradeHistoryData.e === "trade") {
      let updatedRecords = [...marketTradeRecords];
      tradeHistoryData.tH.forEach((trade) => {
        const tradeItem = {
          quantity: parseFloat(trade.quantity).toFixed(2),
          price: parseFloat(trade.price).toFixed(2),
          timestamp: trade.timestamp,
          side: trade.side,
          newTrade: trade.side === "ASK" ? "newask" : "newbid",
        };
        updatedRecords.unshift(tradeItem);
      });
      if (updatedRecords.length > 100)
        updatedRecords = updatedRecords.slice(0, 100);
      setMarketTradeRecords(updatedRecords);
    }
  };

  const serverSentEventForOrderbookAsk = async () => {
    try {
      const response = await axios.get(
        `https://options-socket.paybito.com/oSocketStream/api/depth?symbol=${assetpair}&limit=25`,
        {
          headers: { authorization: `BEARER ${accessToken}` },
        }
      );
      setAskdata([...response.data.ask].sort((a, b) => b.price - a.price));
      setBiddata([...response.data.bid].sort((a, b) => b.price - a.price));

      websocketRef.current = { socket: null, subscribeMessage: null };
      connect();

      websocketRef.current.subscribeMessage = (message) => {
        if (!message) return;
        const orderbookData = message;
        if (orderbookData.e === "depthUpdate") {
          setAskdata((prev) => {
            let updated = [...prev];
            orderbookData.ask?.forEach((ask) => {
              if (ask.action === "NEW" && ask.side === "ASK") {
                updated.push({
                  price: parseFloat(ask.price).toFixed(tradep),
                  amount: parseFloat(ask.quantity).toFixed(tradep),
                  newly: true,
                });
              } else if (ask.action === "CHANGE") {
                updated = updated.map((item) =>
                  item.price === parseFloat(ask.price)
                    ? { ...item, amount: ask.quantity, newly: true }
                    : item
                );
              } else if (ask.action === "DELETE") {
                updated = updated.filter(
                  (item) => item.price !== parseFloat(ask.price)
                );
              }
            });
            return [...updated].sort((a, b) => b.price - a.price).slice(-50);
          });

          setBiddata((prev) => {
            let updated = [...prev];
            orderbookData.bid?.forEach((bid) => {
              if (bid.action === "NEW" && bid.side === "BID") {
                updated.push({
                  price: parseFloat(bid.price).toFixed(tradep),
                  amount: parseFloat(bid.quantity).toFixed(tradep),
                  newly: true,
                });
              } else if (bid.action === "CHANGE") {
                updated = updated.map((item) =>
                  item.price === parseFloat(bid.price)
                    ? { ...item, amount: bid.quantity, newly: true }
                    : item
                );
              } else if (bid.action === "DELETE") {
                updated = updated.filter(
                  (item) => item.price !== parseFloat(bid.price)
                );
              }
            });
            return [...updated].sort((a, b) => b.price - a.price).slice(0, 50);
          });
        }
      };
    } catch (error) {
      console.error("Error fetching order book:", error);
    }
  };

  useEffect(() => {
    setFinalBiddata(biddata);
    setFinalAskdata(askdata);
  }, [biddata, askdata]);

  useEffect(() => {
    serverSentEventForOrderbookAsk();
    return () => disconnect();
  }, [assetpair]);

  const getPrice = (m) => parseFloat(m.price).toFixed(tradep);
  const getAmount = (m) => parseFloat(m.amount).toFixed(tradep);

  const handleSellClick = (data) => setSellData(data);
  const handleBuyClick = (data) => setBuyData(data);

  return (
    <div style={{ marginRight: "2px", marginTop: "11px" }}>
      <div
        className="table_content"
        style={{ height: "40px", overflow: "hidden" }}
      >
        <table style={{ width: "98%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ color: theme === "light" ? "black" : "" }}>
                Amount
                <InfoIcon
                  style={{
                    color: "#a8a1a1",
                    cursor: "pointer",
                    fontSize: "15px",
                  }}
                  onClick={() => setShow(true)}
                />
              </th>
              <th style={{ color: theme === "light" ? "black" : "" }}>Price</th>
              <th
                style={{
                  color: theme === "light" ? "black" : "",
                  textAlign: "center",
                }}
              >
                Total
              </th>
            </tr>
          </thead>
        </table>
      </div>

      <div className="table_content reverse_table" ref={tableContentRef}>
        <table style={{ width: "100%" }}>
          <tbody>
            {askdata.length > 0 ? (
              [...askdata].map((d, i) => (
                <tr key={`ask-${i}`} className={d.newly ? "bg-flash-red" : ""}>
                  <td className="text-end">{getAmount(d)}</td>
                  <td
                    className="text-end"
                    style={{ color: "#F5475D" }}
                    onClick={() => handleSellClick(getPrice(d))}
                  >
                    {getPrice(d)}
                  </td>
                  <td className="text-end">
                    {(getAmount(d) * getPrice(d)).toFixed(tradep)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="text-center">
                  No Data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="sp-highlow">
        {currencySymbols[basecurrency?.toUpperCase()] && (
          <span style={{ color: "#f1b900" }}>
            {currencySymbols[basecurrency?.toUpperCase()]}
          </span>
        )}
        <span style={{ color: "#f1b900", paddingRight: "5px" }}>
          {chartData?.ctp}
        </span>
        <span className="font-sm ml-2" id="pricegreen">
          {toggle ? (
            <span style={{ fontSize: "10px", color: "#21CB80" }}>
              <ArrowUpwardIcon sx={{ fontSize: "12px" }} /> 24H H: {highPrice}
            </span>
          ) : (
            <span style={{ fontSize: "10px", color: "#F5475D" }}>
              <ArrowDownwardIcon sx={{ fontSize: "12px" }} /> 24H L: {lowPrice}
            </span>
          )}
        </span>
      </div>

      <div className="table_content">
        <table style={{ width: "100%" }}>
          <tbody>
            {biddata.length > 0 ? (
              [...biddata].map((d, i) => (
                <tr
                  key={`bid-${i}`}
                  className={d.newly ? "bg-flash-green" : ""}
                >
                  <td className="text-end">{getAmount(d)}</td>
                  <td
                    className="text-end"
                    style={{ color: "#21CB80" }}
                    onClick={() => handleBuyClick(getPrice(d))}
                  >
                    {getPrice(d)}
                  </td>
                  <td className="text-end">
                    {(getAmount(d) * getPrice(d)).toFixed(tradep)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="text-center">
                  No Data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={title}
        description={description}
      />
    </div>
  );
};

export default OptionOrderBook;
